<script setup lang="ts">
import { usePolisGetPolissen } from '@services/polis-public/orval-api/polis-api/polis-api';
import VHeading from '@shared/typography/VHeading/VHeading.vue';
import VMenu from '@shared/component/VMenu/VMenu.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { computed, ref } from 'vue';
import { type NavigationFailure, useRoute } from 'vue-router';

type MenuItem = { polisnummer: string; omschrijving: string; kenmerk?: string };

const props = defineProps<{
  relatienummer: number;
  polisnummer: string;
}>();

const currentRoute = useRoute();

const menu = ref<InstanceType<typeof VMenu>>();

const overlayVisible = computed(() => menu.value?.overlayVisible());

const { isPending, data: polissen } = usePolisGetPolissen(
  computed(() => ({
    relatienummer: props.relatienummer,
    neemRoyementenMee: true,
    neemBeursPakkettenMee: true,
  })),
  {
    query: {
      select: (result) => result.result,
      retry: 3,
      staleTime: 5 * 60 * 1000,
    },
  },
);

const currentPakketpolis = computed(() => {
  if (isPending.value || !polissen.value) return undefined;

  return polissen.value.find(
    (polis) =>
      polis.polisnummer === props.polisnummer ||
      polis.subpolissen.some((sub) => sub.polisnummer === props.polisnummer),
  );
});

const currentPolisIsGeroyeerd = computed(() => {
  if (!currentPakketpolis.value) return undefined;

  if (currentPakketpolis.value.polisnummer === props.polisnummer) {
    return currentPakketpolis.value.isGeroyeerd;
  }

  const currentPolis = currentPakketpolis.value.subpolissen.find(
    (polis) => polis.polisnummer === props.polisnummer,
  );
  return currentPolis?.isGeroyeerd ?? false;
});

const menuItems = computed(() => {
  if (!currentPakketpolis.value || currentPolisIsGeroyeerd.value === undefined) return undefined;

  const items: MenuItem[] = [];

  currentPakketpolis.value.subpolissen
    .filter((subpolis) => subpolis.isGeroyeerd === currentPolisIsGeroyeerd.value)
    .forEach((subpolis) => {
      const { polisnummer, omschrijving, kenmerk } = subpolis;
      items.push({ polisnummer, omschrijving, kenmerk });
    });

  return items;
});

function toggleMenu(event: MouseEvent) {
  if (isPending.value || !polissen.value) return;

  menu.value?.toggle(event);
}

function getItemLink(polisnummer: string) {
  return { name: currentRoute.name!, params: { relatienummer: props.relatienummer, polisnummer } };
}

function onMenuItemClicked(
  event: MouseEvent,
  navigate: (e?: MouseEvent) => Promise<void | NavigationFailure>,
) {
  menu.value?.hide(event);
  navigate(event);
}
</script>
<template>
  <div class="relative w-fit">
    <div
      class="flex flex-row items-center gap-2 rounded px-2 py-1"
      :class="{
        'cursor-pointer': !isPending,
        'transition-colors duration-100 hover:bg-grey-100': !isPending,
      }"
      @click="toggleMenu"
    >
      <VHeading :level="1">Polis {{ polisnummer }}</VHeading>
      <FontAwesomeIcon
        v-if="!isPending"
        :class="[
          'transition-transform',
          {
            '-scale-100': overlayVisible,
          },
        ]"
        icon="fa fa-chevron-down"
        size="xl"
      />
    </div>
    <VMenu
      popup
      ref="menu"
      :model="menuItems"
      append-to="self"
      class="top-full! left-0! max-h-96 w-full overflow-auto"
    >
      <template #item="{ item }">
        <RouterLink
          :to="getItemLink(item.polisnummer)"
          custom
          v-slot="{ isActive, href, navigate }"
        >
          <a
            :href="href"
            @click.stop="(e) => onMenuItemClicked(e, navigate)"
            class="flex flex-col rounded p-1 no-underline transition-colors hover:bg-grey-100"
            :class="{ 'bg-grey-100': isActive }"
          >
            <span class="font-bold text-primary-900">{{ item.polisnummer }}</span>
            <span class="text-xs text-grey-500">{{ item.omschrijving }}</span>
            <span class="text-xs text-grey-500">{{ item.kenmerk ?? '' }}</span>
          </a>
        </RouterLink>
      </template>
    </VMenu>
  </div>
</template>
